import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import {
  UserDetails,
  UpdateUserData,
  UpdateProfilePictureData,
  UpdatePasswordData,
} from "@/types/userDetails";
import { UserType } from "@/types/user";
import { MediaService } from "../media/mediaService";

// Add interface for the API response structure
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export class UserService {
  // Update user information
  // TODO: ska gå mot /users inte /auth här! AUTH BARA FÖR SIGN IN/UP osv
  static async updateUserInfo(
    userData: UpdateUserData,
    userType?: UserType,
  ): Promise<UserDetails> {
    // If admin, use admin endpoint, otherwise use regular user endpoint
    if (userType === UserType.Admin) {
      const response = await api.put<ApiResponse<UserDetails>>(
        "/admin/profile",
        userData,
      );
      return response.data;
    }
    const response = await api.put<ApiResponse<UserDetails>>(
      "/users/updateDetails",
      userData,
    ); // TODO: De returnerar inget på profilePhoto. kolla med backend
    return response.data; // Extract the data property
  }

  // Update profile picture using S3 presigned URLs
  static async updateProfilePicture(
    fileData: UpdateProfilePictureData,
    userType?: UserType,
  ): Promise<UserDetails> {
    const file = fileData.profilePhoto;

    try {
      // 1. Request presigned URL
      // For profile pictures, backend uses req.actor.id, so we can pass empty string or any value
      // Backend will automatically use the authenticated actor's ID
      const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
        "profile",
        "", // Backend will use req.actor.id for profile pictures
        {
          originalName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        },
      );

      // 2. Upload file directly to S3
      await MediaService.uploadFileToS3(uploadUrl, file);

      // 3. Create Media record
      // For profile pictures, backend uses req.actor.id automatically, so we don't pass actorId
      const media = await MediaService.createMediaAfterUpload(
        s3Key,
        {
          originalName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
        },
        {}, // backend sets actorId from req.actor.id for profile pictures when s3Key starts with users/
      );

      // 4. Update status to UPLOADED
      await MediaService.updateMediaStatus(media.id, "UPLOADED");

      // 5. Get updated user details with new profile picture URL
      return await this.getUserDetails(userType);
    } catch (error) {
      throw error;
    }
  }

  // Remove profile picture using S3 Media service
  static async removeProfilePicture(userType?: UserType): Promise<UserDetails> {
    try {
      // Delete profile picture via Media service
      const mediaUserType = userType === UserType.Admin ? "admin" : "user";
      await MediaService.deleteProfilePicture(mediaUserType);

      // Get updated user details
      return await this.getUserDetails(userType);
    } catch (error) {
      throw error;
    }
  }

  // Update password
  static async updatePassword(
    passwordData: UpdatePasswordData,
    userType?: UserType,
  ): Promise<void> {
    // If admin, use admin endpoint, otherwise use regular user endpoint
    if (userType === UserType.Admin) {
      await api.put<void>("/admin/profile/password", passwordData);
      return;
    }
    await api.put<void>("/users/updatePassword", passwordData);
  }

  // Get user details (for settings page)
  // If user is admin, fetches from /admin/profile, otherwise from /users/me
  static async getUserDetails(userType?: UserType): Promise<UserDetails> {
    if (userType === UserType.Admin) {
      const response =
        await api.get<ApiResponse<UserDetails>>("/admin/profile");
      return response.data;
    }
    const response = await api.get<ApiResponse<UserDetails>>("/users/me");
    return response.data; // Extract the data property
  }

  // Update user activity (heartbeat)
  static async updateActivity(): Promise<void> {
    await api.post("/auth/activity");
  }

  // Update showQuickGuide setting
  static async updateShowQuickGuide(
    showQuickGuide: boolean,
  ): Promise<UserDetails> {
    const response = await api.put<ApiResponse<UserDetails>>(
      "/users/updateShowQuickGuide",
      { showQuickGuide },
    );
    return response.data;
  }
}
